import {InputSeparationVariant} from '../parse-input';
import {METADATA_FIELD_TYPE, MetadataField, MetadataSuggestion} from '../interface';
import {useDebouncedSeparationVariants} from './use-debounced-separation-variants';
import {useQueries} from '@tanstack/react-query';
import {apiClient} from '../../../../../utils/api-client';
import {getMetadataFieldNameByLabel} from '../suggestion-helpers';

const REQUEST_LIMIT = 30;

interface Props {
    inputSeparationVariants: InputSeparationVariant[];
    enabled: boolean;
}

interface MetadataSuggestionDTO {
    type: MetadataField;
    value: string;
    displayName: string | null;
}

function metadataSuggestionsRequest(input: string, prefix: string | null) {
    const types: MetadataField[] = (() => {
        if (prefix === 'tag') {
            return [METADATA_FIELD_TYPE.TAG_PUBLIC, METADATA_FIELD_TYPE.TAG_PRIVATE];
        }
        const fieldType = prefix ? getMetadataFieldNameByLabel(prefix) : undefined;
        if (fieldType) {
            return [fieldType as MetadataField];
        }
        return [];
    })();

    return apiClient
        .get<Array<MetadataSuggestionDTO>>({
            path: 'autocomplete-service/api/metadata/suggestions',
            query: {
                input,
                types,
                limit: REQUEST_LIMIT
            }
        })
        .then((result) =>
            result.map(
                (suggestion) =>
                    ({
                        ...suggestion,
                        fieldType: suggestion.type,
                        type: 'metadata'
                    }) as MetadataSuggestion
            )
        );
}

export const useMetadataSuggestions = (props: Props) => {
    const {debouncedSeparationVariants} = useDebouncedSeparationVariants(
        props.inputSeparationVariants
    );

    return useQueries({
        queries: debouncedSeparationVariants.map((variant) => ({
            queryKey: [
                'autocomplete-metadata-suggestions',
                variant.inputPrefix,
                variant.inputValue
            ],
            queryFn: () =>
                metadataSuggestionsRequest(variant.inputValue, variant.inputPrefix || null),
            enabled: props.enabled
        })),
        combine: (result) => ({
            isLoading: result.length && result.every((query) => query.isLoading),
            data: result.map((query) => query.data || []).flat()
        })
    });
};
